<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import { buildConnectUrl } from '@/api/integrations/qbo/index.js'
import { qboProvider } from '@/api/integrations/qbo/provider.js'
import { Button, Alert, Card, Spinner } from '@/components/ui'
import { ArrowRight, Landmark, ShieldCheck } from 'lucide-vue-next'
import { track } from '@/lib/usageTracker'
import { useAuthStore } from '@/stores/authStore'

// --- Interface-design checkpoint ---
// Intent: launch the QuickBooks OAuth handoff for an existing or
// freshly-set-up store. The shell gives the user a recognizable
// "provider connection" surface. The body card shows the acting
// context (store + invoice) and a single primary action.

const route = useRoute()
const store = useAuthStore()
const storeId = ref('')
const invoiceNumber = ref('')
const missingParams = ref(false)
const lastConnectUrl = ref('')
const submitting = ref(false)

function restoreContext() {
  const qStore =
    route.query.storeId?.toString() ||
    route.query.storeID?.toString() ||
    store.selectedStore?.storeId ||
    store.storeID ||
    ''
  const qInv =
    route.query.invoiceNumber?.toString() || store.invoiceNumber || ''
  storeId.value = qStore
  invoiceNumber.value = qInv
  missingParams.value = !(storeId.value && invoiceNumber.value)
}

function persistContext() {
  try {
    const PREFIX = qboProvider.localStoragePrefix
    localStorage.setItem(`${PREFIX}.storeId`, storeId.value)
    localStorage.setItem(`${PREFIX}.invoiceNumber`, invoiceNumber.value)
  } catch {}
}

async function onConnectClick() {
  track('btn:ActivateQbo', 'click')
  if (missingParams.value) return
  try {
    persistContext()
    submitting.value = true
    const url = buildConnectUrl(storeId.value, invoiceNumber.value)
    lastConnectUrl.value = url
    window.location.assign(url)
  } catch (e) {
    console.error('Failed to start QuickBooks connect flow', e)
    alert('Could not open the QuickBooks authorization page. Please verify your Store ID and Invoice Number, then try again.')
  } finally {
    submitting.value = false
  }
}

const computedConnectUrl = computed(() => {
  if (missingParams.value) return ''
  try {
    return buildConnectUrl(storeId.value, invoiceNumber.value)
  } catch {
    return ''
  }
})

const contextItems = computed(() => [
  {
    label: 'Store',
    value: storeId.value || 'Missing from link',
    tone: storeId.value ? 'default' : 'warning',
  },
  {
    label: 'Invoice',
    value: invoiceNumber.value || 'Missing from link',
    tone: invoiceNumber.value ? 'default' : 'warning',
  },
  {
    label: 'Provider',
    value: qboProvider.name,
    tone: 'accent' as const,
  },
])

onMounted(() => {
  restoreContext()
})
</script>

<template>
  <SetupFlowShell
    eyebrow="Connection handoff"
    :title="`Connect ${qboProvider.name}`"
    subtitle="Verify your store and invoice references, then continue to authorize the provider. You'll return to this workspace after Intuit approves access."
    :provider-name="qboProvider.name"
    provider-color="#2ca01c"
    :context-items="contextItems"
  >
    <Card padding="lg" class="activate-panel">
      <div class="activate-panel__head">
        <div class="activate-panel__mark" aria-hidden="true">
          <Landmark :size="18" />
        </div>
        <div>
          <div class="activate-panel__eyebrow">Acting context</div>
          <h2 class="activate-panel__title">{{ qboProvider.name }} handoff</h2>
        </div>
      </div>

      <div class="activate-panel__summary">
        <div class="summary-row">
          <span class="summary-label">Store reference</span>
          <span class="summary-value">{{ storeId || '—' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Invoice number</span>
          <span class="summary-value">{{ invoiceNumber || '—' }}</span>
        </div>
      </div>

      <Alert
        v-if="missingParams"
        id="activateMissing"
        variant="warning"
        title="Missing information"
      >
        Please open this page with both <code>storeId</code> and
        <code>invoiceNumber</code> query parameters.
      </Alert>

      <ul class="activate-panel__benefits">
        <li>
          <ShieldCheck :size="16" aria-hidden="true" />
          <span>You'll be redirected to Intuit to approve access, then returned here.</span>
        </li>
        <li>
          <ShieldCheck :size="16" aria-hidden="true" />
          <span>Your store and invoice references stay bound to the connection.</span>
        </li>
      </ul>

      <div class="activate-panel__actions">
        <Button
          id="connectButton"
          :disabled="missingParams || submitting"
          @click="onConnectClick"
        >
          <Spinner v-if="submitting" size="sm" />
          <span v-if="!submitting">Connect to {{ qboProvider.name }}</span>
          <span v-else>Opening Intuit</span>
          <ArrowRight v-if="!submitting" :size="14" />
        </Button>
        <a
          v-if="!missingParams && computedConnectUrl"
          class="activate-panel__open-link"
          :href="computedConnectUrl"
          title="Open connect link directly"
        >
          Open link directly
        </a>
      </div>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.activate-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activate-panel__head {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.activate-panel__mark {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface-muted);
  color: var(--color-ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.activate-panel__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.activate-panel__title {
  margin: 0.2rem 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--color-ink);
}

.activate-panel__summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.summary-row {
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  background: var(--color-surface-muted);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.summary-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.summary-value {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--color-ink);
  font-variant-numeric: tabular-nums;
  word-break: break-all;
}

.activate-panel__benefits {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.activate-panel__benefits li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--color-ink-soft);
}

.activate-panel__benefits svg {
  color: var(--color-success);
  flex-shrink: 0;
}

.activate-panel__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.activate-panel__actions :deep(button) {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.activate-panel__open-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  font-weight: 500;
  font-size: 0.85rem;
  color: var(--color-ink);
  text-decoration: none;
  background: var(--color-surface);
  transition: border-color 0.15s ease;
}

.activate-panel__open-link:hover {
  border-color: var(--color-border-strong);
  color: var(--color-ink);
}

@media (max-width: 640px) {
  .activate-panel__actions :deep(button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
